/**
 * RulerOverlay - top + left ruler bars overlaying the canvas edges, synced to
 * the React Flow viewport. Reads the viewport transform [x, y, zoom] from the
 * store so ticks/labels track panning and zooming.
 *
 * Rendered INSIDE <ReactFlow> (needs ReactFlowProvider context). pointer-events
 * are disabled so the rulers never block canvas interaction.
 */

import { useStore } from '@xyflow/react'

const RULER_SIZE = 20 // px thickness of each ruler bar
const BASE_STEP = 100 // flow-units between major ticks at zoom = 1

/**
 * Choose a "nice" flow-space interval between major ticks so that on-screen
 * spacing stays readable (~60–140px) regardless of zoom.
 */
function niceStep(zoom: number): number {
  const targetScreenPx = 80
  let step = BASE_STEP
  // Shrink/grow by powers so that step*zoom is near targetScreenPx.
  while (step * zoom < targetScreenPx / 2) step *= 2
  while (step * zoom > targetScreenPx * 2) step /= 2
  // Round to a friendly value.
  const friendly = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000]
  let best = friendly[0]
  let bestDiff = Infinity
  for (const f of friendly) {
    const diff = Math.abs(f * zoom - targetScreenPx)
    if (diff < bestDiff) {
      bestDiff = diff
      best = f
    }
  }
  return best
}

const RULER_BG = 'var(--panel-bg, #ffffff)'
const TICK_COLOR = 'rgba(120,120,120,0.55)'
const MAJOR_TICK_COLOR = 'rgba(80,80,80,0.85)'
const LABEL_COLOR = 'rgba(90,90,90,0.95)'

export default function RulerOverlay() {
  const transform = useStore((s) => s.transform)
  const width = useStore((s) => s.width)
  const height = useStore((s) => s.height)

  const [tx, ty, zoom] = transform
  if (!width || !height) return null

  const step = niceStep(zoom)
  const minorStep = step / 5

  // screen = flow * zoom + translate  →  flow = (screen - translate) / zoom
  const flowAt = (screen: number, translate: number) => (screen - translate) / zoom
  const screenAt = (flow: number, translate: number) => flow * zoom + translate

  // ---- Horizontal (top) ticks ----
  const hTicks: { x: number; label: string; major: boolean }[] = []
  const flowStartX = flowAt(0, tx)
  const flowEndX = flowAt(width, tx)
  const firstMinorX = Math.ceil(flowStartX / minorStep) * minorStep
  for (let f = firstMinorX; f <= flowEndX; f += minorStep) {
    const isMajor = Math.abs(f % step) < 1e-6 || Math.abs((f % step) - step) < 1e-6
    hTicks.push({
      x: screenAt(f, tx),
      label: isMajor ? String(Math.round(f)) : '',
      major: isMajor,
    })
  }

  // ---- Vertical (left) ticks ----
  const vTicks: { y: number; label: string; major: boolean }[] = []
  const flowStartY = flowAt(0, ty)
  const flowEndY = flowAt(height, ty)
  const firstMinorY = Math.ceil(flowStartY / minorStep) * minorStep
  for (let f = firstMinorY; f <= flowEndY; f += minorStep) {
    const isMajor = Math.abs(f % step) < 1e-6 || Math.abs((f % step) - step) < 1e-6
    vTicks.push({
      y: screenAt(f, ty),
      label: isMajor ? String(Math.round(f)) : '',
      major: isMajor,
    })
  }

  return (
    <div
      className="ruler-overlay"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 6,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* Top ruler */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: RULER_SIZE,
          right: 0,
          height: RULER_SIZE,
          background: RULER_BG,
          borderBottom: '1px solid rgba(120,120,120,0.3)',
          overflow: 'hidden',
        }}
      >
        {hTicks.map((t, i) => (
          <div key={i}>
            <div
              style={{
                position: 'absolute',
                left: t.x - RULER_SIZE,
                top: t.major ? RULER_SIZE * 0.35 : RULER_SIZE * 0.6,
                bottom: 0,
                width: 1,
                background: t.major ? MAJOR_TICK_COLOR : TICK_COLOR,
              }}
            />
            {t.label && (
              <span
                style={{
                  position: 'absolute',
                  left: t.x - RULER_SIZE + 2,
                  top: 1,
                  fontSize: 9,
                  color: LABEL_COLOR,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {t.label}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Left ruler */}
      <div
        style={{
          position: 'absolute',
          top: RULER_SIZE,
          left: 0,
          bottom: 0,
          width: RULER_SIZE,
          background: RULER_BG,
          borderRight: '1px solid rgba(120,120,120,0.3)',
          overflow: 'hidden',
        }}
      >
        {vTicks.map((t, i) => (
          <div key={i}>
            <div
              style={{
                position: 'absolute',
                top: t.y - RULER_SIZE,
                left: t.major ? RULER_SIZE * 0.35 : RULER_SIZE * 0.6,
                right: 0,
                height: 1,
                background: t.major ? MAJOR_TICK_COLOR : TICK_COLOR,
              }}
            />
            {t.label && (
              <span
                style={{
                  position: 'absolute',
                  top: t.y - RULER_SIZE + 2,
                  left: 1,
                  fontSize: 9,
                  color: LABEL_COLOR,
                  lineHeight: 1,
                  writingMode: 'vertical-rl',
                  whiteSpace: 'nowrap',
                }}
              >
                {t.label}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Corner box */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: RULER_SIZE,
          height: RULER_SIZE,
          background: RULER_BG,
          borderRight: '1px solid rgba(120,120,120,0.3)',
          borderBottom: '1px solid rgba(120,120,120,0.3)',
        }}
      />
    </div>
  )
}
